import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAgent, useCreateAgent, useUpdateAgent } from '../hooks/useAgents';
import { AgentForm, AgentFormData } from '../components/agent/AgentForm';
import { Button } from '../components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { AgentDetailSkeleton } from '../components/common/LoadingSkeleton';

export default function AgentBuilder() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);

  const { data: agent, isLoading: isLoadingAgent, error: loadError } = useAgent(id);
  const createAgent = useCreateAgent();
  const updateAgent = useUpdateAgent();

  const handleSubmit = async (data: AgentFormData) => {
    // Parse configuration JSON string to object
    let config: Record<string, unknown> | undefined;
    if (data.configuration && data.configuration.trim()) {
      try {
        config = JSON.parse(data.configuration);
      } catch {
        // Validation should have caught this, but fallback to undefined
        config = undefined;
      }
    }

    // Parse comma-separated tags into a clean array (drop blanks).
    const tags = (data.tags ?? '')
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);

    const payload = {
      name: data.name,
      description: data.description || undefined,
      type: data.type,
      config,
      tags,
    };

    try {
      if (isEditMode && id) {
        await updateAgent.mutateAsync({ id, data: payload });
        navigate(`/agents/${id}`);
      } else {
        const newAgent = await createAgent.mutateAsync(payload);
        navigate(`/agents/${newAgent.id}`);
      }
    } catch {
      // Error is already handled by the mutation's onError callback
    }
  };

  // Loading state for edit mode
  if (isEditMode && isLoadingAgent) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4">
        <div className="max-w-3xl mx-auto py-8">
          <AgentDetailSkeleton />
        </div>
      </div>
    );
  }

  // Error state for edit mode
  if (isEditMode && (loadError || !agent)) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4">
        <div className="max-w-3xl mx-auto py-8">
          <Card>
            <CardContent className="py-12 text-center">
              <div className="text-6xl mb-4">❌</div>
              <CardTitle className="mb-2">Agent Not Found</CardTitle>
              <CardDescription className="mb-4">
                The agent you're trying to edit doesn't exist or has been deleted.
              </CardDescription>
              <Button onClick={() => navigate('/agents')}>
                Back to Agents
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Convert config object to JSON string for the editor
  const configString = agent?.config && Object.keys(agent.config).length > 0
    ? JSON.stringify(agent.config, null, 2)
    : '';

  const defaultValues = agent
    ? {
        name: agent.name,
        description: agent.description || '',
        type: agent.type,
        configuration: configString,
        tags: '',
      }
    : undefined;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4">
      <div className="max-w-3xl mx-auto py-8">
        <div className="mb-6">
          <Link
            to={isEditMode ? `/agents/${id}` : '/agents'}
            className="text-sm text-primary hover:underline mb-4 inline-block"
          >
            ← {isEditMode ? 'Back to Agent' : 'Back to Agents'}
          </Link>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>
              {isEditMode ? 'Edit Agent' : 'Create New Agent'}
            </CardTitle>
            <CardDescription>
              {isEditMode
                ? 'Update your agent configuration'
                : 'Configure your new AI agent'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <AgentForm
              key={agent?.id || 'new'}
              defaultValues={defaultValues}
              onSubmit={handleSubmit}
              isSubmitting={createAgent.isPending || updateAgent.isPending}
              submitLabel={isEditMode ? 'Update Agent' : 'Create Agent'}
            />
          </CardContent>
          <CardFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(isEditMode ? `/agents/${id}` : '/agents')}
              disabled={createAgent.isPending || updateAgent.isPending}
              className="w-full"
            >
              Cancel
            </Button>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
}
